/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  wizardSelectionToType,
  FACEBOOK_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  SMS_CHANNEL_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
} from '@ibm-watson/shared/modules/channels/util/channelTypes';
import {
  selectOnboardingTaskListStatus,
  selectPlan,
  selectIsNewIa
} from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import {
  addMessage,
  ToastStatus
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { setAssistantPreferences } from '@ibm-watson/shared/modules/ia/store/preferences/actions';
import {
  selectTaskListStatusPreference,
  selectWizardPreference
} from '@ibm-watson/shared/modules/ia/store/preferences/selectors';
import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import {
  // New IA Onboarding Task List arrays
  GET_STARTED_TASKS,
  CREATE_CONVERSATION_TASKS,
  SHARE_WITH_TEAM_TASKS,
  // New IA Task List arrays
  ENHANCE_ASSISTANT_TASKS,
  TEST_ASSISTANT_TASKS,
  COMMON_DEPLOY_TASKS,
  DEFAULT_DEPLOY_TASKS,
  WEB_CHAT_TASKS,
  PHONE_TASKS,
  SMS_TASKS,
  SLACK_TASKS,
  FACEBOOK_TASKS,
  WHATS_APP_TASKS,
  // Other
  ALL_TASK_NAMES
} from '@ibm-watson/shared/modules/ia/util/task-list/constants';
import { updatedObjectEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { PlanName } from '../../../ts/plan';
import { TaskListKey } from '../../../ts/preferences';
import { AsyncAppThunk } from '../../../ts/state';
import { updateInstancePreferences } from '../store/instance/actions';

/**
 * Updates the taskListStatuses preferences for the given assistant without
 * overwriting other assistant preferences
 * @param {string} taskId The task key/id
 * @param {bool} newStatus The new status of the task (can be false if user
 * toggles a self-check task to incomplete)
 * @returns {void}
 */
export const setTaskListPreferences =
  (taskId: string, newStatus: boolean): AsyncAppThunk =>
  (dispatch, getState) => {
    const assistantId = getAssistantId(getState());

    // The task for completing the onboarding is set not at the assistant level,
    // but at the instance level.
    if (taskId === TaskListKey.HAS_FINISHED_ONBOARDING_TASK_LIST) {
      dispatch(
        updateInstancePreferences({
          taskListStatus: {
            [taskId]: newStatus
          }
        })
      );
    } else {
      dispatch(
        setAssistantPreferences({
          assistant_id: assistantId,
          preferences: {
            taskListStatus: {
              [taskId]: newStatus
            }
          }
        })
      );
    }
  };

/**
 * Depending on the integration selected in the Onboarding Wizard, return a list
 * of 'Deploy your assistant' tasks specific to the selected integration
 * @returns personalizedDeployTasks Array of personalized tasks
 */
export const getPersonalizedDeployTasks = () => (dispatch, getState) => {
  const assistantId = getAssistantId(getState());
  const plan = selectPlan(getState());
  const preferredIntegrationTemplateType =
    wizardSelectionToType[
      selectWizardPreference(getState(), assistantId)?.channel
    ];

  // If on Standard plan or CP4D, only include the "Publish your latest content
  // to the live environment task" in the "Deploy your assistant" section
  if (
    plan === PlanName.STANDARD ||
    isCpd ||
    // If on Lite, Premium, or Premium User Based plans, don't show any
    // Phone integration tasks
    (preferredIntegrationTemplateType === PHONE_CHANNEL_TYPE &&
      (plan === PlanName.LITE ||
        plan === PlanName.PREMIUM ||
        plan === PlanName.PREMIUM_USERBASED))
  ) {
    return [].concat(COMMON_DEPLOY_TASKS);
  }

  switch (preferredIntegrationTemplateType) {
    case WEBCHAT_CHANNEL_TYPE:
      return [].concat(WEB_CHAT_TASKS, COMMON_DEPLOY_TASKS);

    case PHONE_CHANNEL_TYPE:
      return [].concat(PHONE_TASKS, COMMON_DEPLOY_TASKS);

    case SMS_CHANNEL_TYPE:
      return [].concat(SMS_TASKS, COMMON_DEPLOY_TASKS);

    case SLACK_CHANNEL_TYPE:
      return [].concat(SLACK_TASKS, COMMON_DEPLOY_TASKS);

    case FACEBOOK_CHANNEL_TYPE:
      return [].concat(FACEBOOK_TASKS, COMMON_DEPLOY_TASKS);

    case WHATSAPP_CHANNEL_TYPE:
      return [].concat(WHATS_APP_TASKS, COMMON_DEPLOY_TASKS);

    default:
      return [].concat(DEFAULT_DEPLOY_TASKS, COMMON_DEPLOY_TASKS);
  }
};

/**
 * Updates the completion status for a given task, sends a Segment event, and
 * shows a success toast notification
 * @param {string} taskId The task key/id
 * @param {bool} newStatus The new status that the task should be updated to
 * @param {string=} taskName The name of the task, if the caller wants to specify it (e.g. if the task name
 * in the enumeration has a variable that needs to be inserted in the task name)
 */
export const updateTaskCompletionStatus =
  (taskId: string, newStatus: boolean, taskName?: string) =>
  (dispatch, getState): void => {
    const assistantId = getAssistantId(getState());
    const taskListStatuses =
      selectTaskListStatusPreference(getState(), assistantId) || {};
    const hasFinishedOnboardingTaskList = selectOnboardingTaskListStatus(
      getState()
    );
    taskName = taskName || ALL_TASK_NAMES[taskId];

    const storageStatus = taskListStatuses[taskId];
    const hasOneActionStatus =
      taskListStatuses[TaskListKey.HAS_CREATED_FIRST_ACTION];
    const hasMultipleActionsStatus =
      taskListStatuses[TaskListKey.HAS_BUILT_SECOND_ACTION];
    const PERSONALIZED_DEPLOY_ASSISTANT_TASKS = dispatch(
      getPersonalizedDeployTasks()
    );

    // If not in new IA, don't mark task complete
    if (!selectIsNewIa(getState())) {
      return;
    }

    // If current task is a dashboard task but the onboarding task list isn't
    // even completed yet, don't mark task complete
    if (
      !hasFinishedOnboardingTaskList &&
      (ENHANCE_ASSISTANT_TASKS.find(task => task.key === taskId) ||
        TEST_ASSISTANT_TASKS.find(task => task.key === taskId) ||
        PERSONALIZED_DEPLOY_ASSISTANT_TASKS.find(task => task.key === taskId))
    ) {
      return;
    }

    // If current task is an onboarding task but the onboarding task list is
    // already complete, don't mark task complete
    else if (
      hasFinishedOnboardingTaskList &&
      (GET_STARTED_TASKS.find(task => task.key === taskId) ||
        CREATE_CONVERSATION_TASKS.find(task => task.key === taskId) ||
        SHARE_WITH_TEAM_TASKS.find(task => task.key === taskId))
    ) {
      // If 'Create your first action' is already marked complete in local
      // storage, show 'Build your second action' success toast notification
      // (meaning at least two actions exist on the assistant)
      // Don't show the 'Build your second action' success toast notification if
      // onboarding task list isn't completed yet
      if (
        taskId === TaskListKey.HAS_CREATED_FIRST_ACTION &&
        hasOneActionStatus &&
        !hasMultipleActionsStatus
      ) {
        taskId = TaskListKey.HAS_BUILT_SECOND_ACTION;
        dispatch(setTaskListPreferences(taskId, newStatus));

        updatedObjectEvents.taskList({
          object: `taskList-${assistantId}`,
          process: 'task list item completed: ' + newStatus,
          category: taskId,
          resultValue: 'success',
          successFlag: true
        });
        dispatch(
          addMessage({
            message: `You have completed the "${taskName}" task`,
            status: ToastStatus.SUCCESS
          })
        );
      } else {
        return;
      }
    }

    // If it was false, and now it's true, send a segment event to record this,
    // show a toast notification, and store the status in Server
    else if (!storageStatus && newStatus) {
      dispatch(setTaskListPreferences(taskId, newStatus));

      updatedObjectEvents.taskList({
        object: `taskList-${assistantId}`,
        process: 'task list item completed: ' + newStatus,
        category: taskId,
        resultValue: 'success',
        successFlag: true
      });
      dispatch(
        addMessage({
          message: `You have completed the "${taskName}" task`,
          status: ToastStatus.SUCCESS
        })
      );
    }

    // If the status was true, and now it's false (toggling a task to incomplete),
    // set the task to the newStatus
    else if (storageStatus && !newStatus) {
      dispatch(setTaskListPreferences(taskId, newStatus));
    }
  };

/**
 * Updates the completion status for a given task, sends a Segment event, and
 * shows a success toast notification
 * @param {string} taskId The task key/id
 * @param {bool} newStatus The new status that the task should be updated to
 * @param {string=} taskName The name of the task, if the caller wants to specify it (e.g. if the task name
 * in the enumeration has a variable that needs to be inserted in the task name)
 */
export const updateTaskCompletionStatusThink =
  (taskId: string, newStatus: boolean, taskName?: string) =>
  (dispatch, getState): void => {
    const state = getState();
    const assistantId = getAssistantId(state);
    const taskListStatuses =
      selectTaskListStatusPreference(state, assistantId) || {};
    taskName = taskName || ALL_TASK_NAMES[taskId];
    const storageStatus = taskListStatuses[taskId];

    // If it was false, and now it's true, send a segment event to record this,
    // show a toast notification, and store the status in Server
    if (!storageStatus && newStatus) {
      dispatch(setTaskListPreferences(taskId, newStatus));

      updatedObjectEvents.taskList({
        object: `taskList-${assistantId}`,
        process: 'task list item completed: ' + newStatus,
        category: taskId,
        resultValue: 'success',
        successFlag: true
      });
      dispatch(
        addMessage({
          message: `You have completed the "${taskName}" task`,
          status: ToastStatus.SUCCESS
        })
      );
    }

    // If the status was true, and now it's false (toggling a task to incomplete),
    // set the task to the newStatus
    else if (storageStatus && !newStatus) {
      dispatch(setTaskListPreferences(taskId, newStatus));
    }
  };
